import {ChangeEvent, FunctionComponent, useEffect, useState} from 'react';
import styled from 'styled-components';
import { getCodeFromNumber } from '../utils/Phone';

const Style = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  background-color: #fff;
  border: 1px solid #e9eef3;
  overflow: hidden;
  color: #9b9b9b;

  &:focus,
  &:focus-within {
    outline: 1px dotted #212121;
    outline: 5px auto -webkit-focus-ring-color;
  }

  .ccs-region {
    display: flex;
    height: 100%;
    width: 32px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #ebebeb;
    font-size: 12px;
  }

  .ccs-plus {
    padding: 0px 4px;
  }

  .ccs-input {
    input {
      height: 100%;
      width: 100%;
      padding-left: 4px;
      padding-right: 0px;
      border: 0px;
      background-color: transparent;
      outline: none;
    }
  }

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

type Country = {
  code: number;
  region: string;
  country: string;
};

type Props = {
  code: number | null;
  phone: string;
  onChange: (newValue: number | ChangeEvent<Element> | null) => void;
};

export const getCountryByCode = (code: number, phone: string): Country | null => {
  let country = countries.find((x) => x.code === code) || null;
  if (country) {
    const realRegion = getCodeFromNumber(phone, country.region);
    country = realRegion ? countries.find((x) => x.region === realRegion) || country : country;
  }
  return country;
};

export const getCodeByCountryRegion = (twoLetterCountry: string): number | null => {
  return (
    countries.find((x) => x.region.toLowerCase() === twoLetterCountry.toLowerCase())?.code || null
  );
};

const CountryCodeSelector: FunctionComponent<Props> = ({ code, phone, onChange }) => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  useEffect(() => {
    if (code && code > 0) {
      setSelectedCountry(getCountryByCode(code, phone));
    } else {
      setSelectedCountry(null);
    }
  }, [code, phone]);

  return (
    <Style>
      <div className="ccs-region">{selectedCountry ? selectedCountry.region : '?'}</div>
      <div className="ccs-plus">+</div>
      <div className="ccs-input">
        <input
          style={{ height: 64 }}
          type="number"
          autoComplete="tel-country-code"
          value={code || ''}
          onChange={(e) =>
            e.currentTarget.value.length > 0
              ? onChange(parseInt(e.currentTarget.value, 10))
              : onChange(null)
          }
        />
      </div>
    </Style>
  );
};

export default CountryCodeSelector;

const countries: Country[] = [
  {
    code: 1,
    region: 'US',
    country: 'United States',
  },
  {
    code: 1,
    region: 'AG',
    country: 'Antigua and Barbuda',
  },
  {
    code: 1,
    region: 'AI',
    country: 'Anguilla',
  },
  {
    code: 1,
    region: 'AS',
    country: 'American Samoa',
  },
  {
    code: 1,
    region: 'BB',
    country: 'Barbados',
  },
  {
    code: 1,
    region: 'BM',
    country: 'Bermuda',
  },
  {
    code: 1,
    region: 'BS',
    country: 'Bahamas',
  },
  {
    code: 1,
    region: 'CA',
    country: 'Canada',
  },
  {
    code: 1,
    region: 'DM',
    country: 'Dominica',
  },
  {
    code: 1,
    region: 'DO',
    country: 'Dominican Republic',
  },
  {
    code: 1,
    region: 'GD',
    country: 'Grenada',
  },
  {
    code: 1,
    region: 'GU',
    country: 'Guam',
  },
  {
    code: 1,
    region: 'JM',
    country: 'Jamaica',
  },
  {
    code: 1,
    region: 'KN',
    country: 'Saint Kitts and Nevis',
  },
  {
    code: 1,
    region: 'KY',
    country: 'Cayman Islands',
  },
  {
    code: 1,
    region: 'LC',
    country: 'Saint Lucia',
  },
  {
    code: 1,
    region: 'MP',
    country: 'Northern Mariana Islands',
  },
  {
    code: 1,
    region: 'MS',
    country: 'Montserrat',
  },
  {
    code: 1,
    region: 'PR',
    country: 'Puerto Rico',
  },
  {
    code: 1,
    region: 'SX',
    country: 'Sint Maarten',
  },
  {
    code: 1,
    region: 'TC',
    country: 'Turks and Caicos Islands',
  },
  {
    code: 1,
    region: 'TT',
    country: 'Trinidad and Tobago',
  },
  {
    code: 1,
    region: 'VC',
    country: 'Saint Vincent and the Grenadines',
  },
  {
    code: 1,
    region: 'VG',
    country: 'British Virgin Islands',
  },
  {
    code: 1,
    region: 'VI',
    country: 'U.S. Virgin Islands',
  },
  {
    code: 7,
    region: 'RU',
    country: 'Russia',
  },
  {
    code: 7,
    region: 'KZ',
    country: 'Kazakhstan',
  },
  {
    code: 20,
    region: 'EG',
    country: 'Egypt',
  },
  {
    code: 27,
    region: 'ZA',
    country: 'South Africa',
  },
  {
    code: 30,
    region: 'GR',
    country: 'Greece',
  },
  {
    code: 31,
    region: 'NL',
    country: 'Netherlands',
  },
  {
    code: 32,
    region: 'BE',
    country: 'Belgium',
  },
  {
    code: 33,
    region: 'FR',
    country: 'France',
  },
  {
    code: 34,
    region: 'ES',
    country: 'Spain',
  },
  {
    code: 36,
    region: 'HU',
    country: 'Hungary',
  },
  {
    code: 39,
    region: 'IT',
    country: 'Italy',
  },
  {
    code: 39,
    region: 'VA',
    country: 'Vatican',
  },
  {
    code: 40,
    region: 'RO',
    country: 'Romania',
  },
  {
    code: 41,
    region: 'CH',
    country: 'Switzerland',
  },
  {
    code: 43,
    region: 'AT',
    country: 'Austria',
  },
  {
    code: 44,
    region: 'GB',
    country: 'United Kingdom',
  },
  {
    code: 44,
    region: 'GG',
    country: 'Guernsey',
  },
  {
    code: 44,
    region: 'IM',
    country: 'Isle of Man',
  },
  {
    code: 44,
    region: 'JE',
    country: 'Jersey',
  },
  {
    code: 45,
    region: 'DK',
    country: 'Denmark',
  },
  {
    code: 46,
    region: 'SE',
    country: 'Sweden',
  },
  {
    code: 47,
    region: 'NO',
    country: 'Norway',
  },
  {
    code: 47,
    region: 'SJ',
    country: 'Svalbard and Jan Mayen',
  },
  {
    code: 48,
    region: 'PL',
    country: 'Poland',
  },
  {
    code: 49,
    region: 'DE',
    country: 'Germany',
  },
  {
    code: 51,
    region: 'PE',
    country: 'Peru',
  },
  {
    code: 52,
    region: 'MX',
    country: 'Mexico',
  },
  {
    code: 53,
    region: 'CU',
    country: 'Cuba',
  },
  {
    code: 54,
    region: 'AR',
    country: 'Argentina',
  },
  {
    code: 55,
    region: 'BR',
    country: 'Brazil',
  },
  {
    code: 56,
    region: 'CL',
    country: 'Chile',
  },
  {
    code: 57,
    region: 'CO',
    country: 'Colombia',
  },
  {
    code: 58,
    region: 'VE',
    country: 'Venezuela',
  },
  {
    code: 60,
    region: 'MY',
    country: 'Malaysia',
  },
  {
    code: 61,
    region: 'AU',
    country: 'Australia',
  },
  {
    code: 61,
    region: 'CC',
    country: 'Cocos Islands',
  },
  {
    code: 61,
    region: 'CX',
    country: 'Christmas Island',
  },
  {
    code: 62,
    region: 'ID',
    country: 'Indonesia',
  },
  {
    code: 63,
    region: 'PH',
    country: 'Philippines',
  },
  {
    code: 64,
    region: 'NZ',
    country: 'New Zealand',
  },
  {
    code: 65,
    region: 'SG',
    country: 'Singapore',
  },
  {
    code: 66,
    region: 'TH',
    country: 'Thailand',
  },
  {
    code: 81,
    region: 'JP',
    country: 'Japan',
  },
  {
    code: 82,
    region: 'KR',
    country: 'South Korea',
  },
  {
    code: 84,
    region: 'VN',
    country: 'Vietnam',
  },
  {
    code: 86,
    region: 'CN',
    country: 'China',
  },
  {
    code: 90,
    region: 'TR',
    country: 'Turkey',
  },
  {
    code: 91,
    region: 'IN',
    country: 'India',
  },
  {
    code: 92,
    region: 'PK',
    country: 'Pakistan',
  },
  {
    code: 93,
    region: 'AF',
    country: 'Afghanistan',
  },
  {
    code: 94,
    region: 'LK',
    country: 'Sri Lanka',
  },
  {
    code: 95,
    region: 'MM',
    country: 'Myanmar',
  },
  {
    code: 98,
    region: 'IR',
    country: 'Iran',
  },
  {
    code: 211,
    region: 'SS',
    country: 'South Sudan',
  },
  {
    code: 212,
    region: 'MA',
    country: 'Morocco',
  },
  {
    code: 212,
    region: 'EH',
    country: 'Western Sahara',
  },
  {
    code: 213,
    region: 'DZ',
    country: 'Algeria',
  },
  {
    code: 216,
    region: 'TN',
    country: 'Tunisia',
  },
  {
    code: 218,
    region: 'LY',
    country: 'Libya',
  },
  {
    code: 220,
    region: 'GM',
    country: 'Gambia',
  },
  {
    code: 221,
    region: 'SN',
    country: 'Senegal',
  },
  {
    code: 222,
    region: 'MR',
    country: 'Mauritania',
  },
  {
    code: 223,
    region: 'ML',
    country: 'Mali',
  },
  {
    code: 224,
    region: 'GN',
    country: 'Guinea',
  },
  {
    code: 225,
    region: 'CI',
    country: 'Ivory Coast',
  },
  {
    code: 226,
    region: 'BF',
    country: 'Burkina Faso',
  },
  {
    code: 227,
    region: 'NE',
    country: 'Niger',
  },
  {
    code: 228,
    region: 'TG',
    country: 'Togo',
  },
  {
    code: 229,
    region: 'BJ',
    country: 'Benin',
  },
  {
    code: 230,
    region: 'MU',
    country: 'Mauritius',
  },
  {
    code: 231,
    region: 'LR',
    country: 'Liberia',
  },
  {
    code: 232,
    region: 'SL',
    country: 'Sierra Leone',
  },
  {
    code: 233,
    region: 'GH',
    country: 'Ghana',
  },
  {
    code: 234,
    region: 'NG',
    country: 'Nigeria',
  },
  {
    code: 235,
    region: 'TD',
    country: 'Chad',
  },
  {
    code: 236,
    region: 'CF',
    country: 'Central African Republic',
  },
  {
    code: 237,
    region: 'CM',
    country: 'Cameroon',
  },
  {
    code: 238,
    region: 'CV',
    country: 'Cape Verde',
  },
  {
    code: 239,
    region: 'ST',
    country: 'Sao Tome and Principe',
  },
  {
    code: 240,
    region: 'GQ',
    country: 'Equatorial Guinea',
  },
  {
    code: 241,
    region: 'GA',
    country: 'Gabon',
  },
  {
    code: 242,
    region: 'CG',
    country: 'Republic of the Congo',
  },
  {
    code: 243,
    region: 'CD',
    country: 'Democratic Republic of the Congo',
  },
  {
    code: 244,
    region: 'AO',
    country: 'Angola',
  },
  {
    code: 245,
    region: 'GW',
    country: 'Guinea-Bissau',
  },
  {
    code: 246,
    region: 'IO',
    country: 'British Indian Ocean Territory',
  },
  {
    code: 247,
    region: 'AC',
    country: '-',
  },
  {
    code: 248,
    region: 'SC',
    country: 'Seychelles',
  },
  {
    code: 249,
    region: 'SD',
    country: 'Sudan',
  },
  {
    code: 250,
    region: 'RW',
    country: 'Rwanda',
  },
  {
    code: 251,
    region: 'ET',
    country: 'Ethiopia',
  },
  {
    code: 252,
    region: 'SO',
    country: 'Somalia',
  },
  {
    code: 253,
    region: 'DJ',
    country: 'Djibouti',
  },
  {
    code: 254,
    region: 'KE',
    country: 'Kenya',
  },
  {
    code: 255,
    region: 'TZ',
    country: 'Tanzania',
  },
  {
    code: 256,
    region: 'UG',
    country: 'Uganda',
  },
  {
    code: 257,
    region: 'BI',
    country: 'Burundi',
  },
  {
    code: 258,
    region: 'MZ',
    country: 'Mozambique',
  },
  {
    code: 260,
    region: 'ZM',
    country: 'Zambia',
  },
  {
    code: 261,
    region: 'MG',
    country: 'Madagascar',
  },
  {
    code: 262,
    region: 'RE',
    country: 'Reunion',
  },
  {
    code: 262,
    region: 'YT',
    country: 'Mayotte',
  },
  {
    code: 263,
    region: 'ZW',
    country: 'Zimbabwe',
  },
  {
    code: 264,
    region: 'NA',
    country: 'Namibia',
  },
  {
    code: 265,
    region: 'MW',
    country: 'Malawi',
  },
  {
    code: 266,
    region: 'LS',
    country: 'Lesotho',
  },
  {
    code: 267,
    region: 'BW',
    country: 'Botswana',
  },
  {
    code: 268,
    region: 'SZ',
    country: 'Swaziland',
  },
  {
    code: 269,
    region: 'KM',
    country: 'Comoros',
  },
  {
    code: 290,
    region: 'SH',
    country: 'Saint Helena',
  },
  {
    code: 290,
    region: 'TA',
    country: '-',
  },
  {
    code: 291,
    region: 'ER',
    country: 'Eritrea',
  },
  {
    code: 297,
    region: 'AW',
    country: 'Aruba',
  },
  {
    code: 298,
    region: 'FO',
    country: 'Faroe Islands',
  },
  {
    code: 299,
    region: 'GL',
    country: 'Greenland',
  },
  {
    code: 350,
    region: 'GI',
    country: 'Gibraltar',
  },
  {
    code: 351,
    region: 'PT',
    country: 'Portugal',
  },
  {
    code: 352,
    region: 'LU',
    country: 'Luxembourg',
  },
  {
    code: 353,
    region: 'IE',
    country: 'Ireland',
  },
  {
    code: 354,
    region: 'IS',
    country: 'Iceland',
  },
  {
    code: 355,
    region: 'AL',
    country: 'Albania',
  },
  {
    code: 356,
    region: 'MT',
    country: 'Malta',
  },
  {
    code: 357,
    region: 'CY',
    country: 'Cyprus',
  },
  {
    code: 358,
    region: 'FI',
    country: 'Finland',
  },
  {
    code: 358,
    region: 'AX',
    country: '-',
  },
  {
    code: 359,
    region: 'BG',
    country: 'Bulgaria',
  },
  {
    code: 370,
    region: 'LT',
    country: 'Lithuania',
  },
  {
    code: 371,
    region: 'LV',
    country: 'Latvia',
  },
  {
    code: 372,
    region: 'EE',
    country: 'Estonia',
  },
  {
    code: 373,
    region: 'MD',
    country: 'Moldova',
  },
  {
    code: 374,
    region: 'AM',
    country: 'Armenia',
  },
  {
    code: 375,
    region: 'BY',
    country: 'Belarus',
  },
  {
    code: 376,
    region: 'AD',
    country: 'Andorra',
  },
  {
    code: 377,
    region: 'MC',
    country: 'Monaco',
  },
  {
    code: 378,
    region: 'SM',
    country: 'San Marino',
  },
  {
    code: 380,
    region: 'UA',
    country: 'Ukraine',
  },
  {
    code: 381,
    region: 'RS',
    country: 'Serbia',
  },
  {
    code: 382,
    region: 'ME',
    country: 'Montenegro',
  },
  {
    code: 383,
    region: 'XK',
    country: 'Kosovo',
  },
  {
    code: 385,
    region: 'HR',
    country: 'Croatia',
  },
  {
    code: 386,
    region: 'SI',
    country: 'Slovenia',
  },
  {
    code: 387,
    region: 'BA',
    country: 'Bosnia and Herzegovina',
  },
  {
    code: 389,
    region: 'MK',
    country: 'Macedonia',
  },
  {
    code: 420,
    region: 'CZ',
    country: 'Czech Republic',
  },
  {
    code: 421,
    region: 'SK',
    country: 'Slovakia',
  },
  {
    code: 423,
    region: 'LI',
    country: 'Liechtenstein',
  },
  {
    code: 500,
    region: 'FK',
    country: 'Falkland Islands',
  },
  {
    code: 501,
    region: 'BZ',
    country: 'Belize',
  },
  {
    code: 502,
    region: 'GT',
    country: 'Guatemala',
  },
  {
    code: 503,
    region: 'SV',
    country: 'El Salvador',
  },
  {
    code: 504,
    region: 'HN',
    country: 'Honduras',
  },
  {
    code: 505,
    region: 'NI',
    country: 'Nicaragua',
  },
  {
    code: 506,
    region: 'CR',
    country: 'Costa Rica',
  },
  {
    code: 507,
    region: 'PA',
    country: 'Panama',
  },
  {
    code: 508,
    region: 'PM',
    country: 'Saint Pierre and Miquelon',
  },
  {
    code: 509,
    region: 'HT',
    country: 'Haiti',
  },
  {
    code: 590,
    region: 'GP',
    country: '-',
  },
  {
    code: 590,
    region: 'BL',
    country: 'Saint Barthelemy',
  },
  {
    code: 590,
    region: 'MF',
    country: 'Saint Martin',
  },
  {
    code: 591,
    region: 'BO',
    country: 'Bolivia',
  },
  {
    code: 592,
    region: 'GY',
    country: 'Guyana',
  },
  {
    code: 593,
    region: 'EC',
    country: 'Ecuador',
  },
  {
    code: 594,
    region: 'GF',
    country: '-',
  },
  {
    code: 595,
    region: 'PY',
    country: 'Paraguay',
  },
  {
    code: 596,
    region: 'MQ',
    country: '-',
  },
  {
    code: 597,
    region: 'SR',
    country: 'Suriname',
  },
  {
    code: 598,
    region: 'UY',
    country: 'Uruguay',
  },
  {
    code: 599,
    region: 'CW',
    country: 'Curacao',
  },
  {
    code: 599,
    region: 'BQ',
    country: '-',
  },
  {
    code: 670,
    region: 'TL',
    country: 'East Timor',
  },
  {
    code: 672,
    region: 'NF',
    country: '-',
  },
  {
    code: 673,
    region: 'BN',
    country: 'Brunei',
  },
  {
    code: 674,
    region: 'NR',
    country: 'Nauru',
  },
  {
    code: 675,
    region: 'PG',
    country: 'Papua New Guinea',
  },
  {
    code: 676,
    region: 'TO',
    country: 'Tonga',
  },
  {
    code: 677,
    region: 'SB',
    country: 'Solomon Islands',
  },
  {
    code: 678,
    region: 'VU',
    country: 'Vanuatu',
  },
  {
    code: 679,
    region: 'FJ',
    country: 'Fiji',
  },
  {
    code: 680,
    region: 'PW',
    country: 'Palau',
  },
  {
    code: 681,
    region: 'WF',
    country: 'Wallis and Futuna',
  },
  {
    code: 682,
    region: 'CK',
    country: 'Cook Islands',
  },
  {
    code: 683,
    region: 'NU',
    country: 'Niue',
  },
  {
    code: 685,
    region: 'WS',
    country: 'Samoa',
  },
  {
    code: 686,
    region: 'KI',
    country: 'Kiribati',
  },
  {
    code: 687,
    region: 'NC',
    country: 'New Caledonia',
  },
  {
    code: 688,
    region: 'TV',
    country: 'Tuvalu',
  },
  {
    code: 689,
    region: 'PF',
    country: 'French Polynesia',
  },
  {
    code: 690,
    region: 'TK',
    country: 'Tokelau',
  },
  {
    code: 691,
    region: 'FM',
    country: 'Micronesia',
  },
  {
    code: 692,
    region: 'MH',
    country: 'Marshall Islands',
  },
  {
    code: 800,
    region: '001',
    country: '-',
  },
  {
    code: 808,
    region: '001',
    country: '-',
  },
  {
    code: 850,
    region: 'KP',
    country: 'North Korea',
  },
  {
    code: 852,
    region: 'HK',
    country: 'Hong Kong',
  },
  {
    code: 853,
    region: 'MO',
    country: 'Macau',
  },
  {
    code: 855,
    region: 'KH',
    country: 'Cambodia',
  },
  {
    code: 856,
    region: 'LA',
    country: 'Laos',
  },
  {
    code: 870,
    region: '001',
    country: '-',
  },
  {
    code: 878,
    region: '001',
    country: '-',
  },
  {
    code: 880,
    region: 'BD',
    country: 'Bangladesh',
  },
  {
    code: 881,
    region: '001',
    country: '-',
  },
  {
    code: 882,
    region: '001',
    country: '-',
  },
  {
    code: 883,
    region: '001',
    country: '-',
  },
  {
    code: 886,
    region: 'TW',
    country: 'Taiwan',
  },
  {
    code: 888,
    region: '001',
    country: '-',
  },
  {
    code: 960,
    region: 'MV',
    country: 'Maldives',
  },
  {
    code: 961,
    region: 'LB',
    country: 'Lebanon',
  },
  {
    code: 962,
    region: 'JO',
    country: 'Jordan',
  },
  {
    code: 963,
    region: 'SY',
    country: 'Syria',
  },
  {
    code: 964,
    region: 'IQ',
    country: 'Iraq',
  },
  {
    code: 965,
    region: 'KW',
    country: 'Kuwait',
  },
  {
    code: 966,
    region: 'SA',
    country: 'Saudi Arabia',
  },
  {
    code: 967,
    region: 'YE',
    country: 'Yemen',
  },
  {
    code: 968,
    region: 'OM',
    country: 'Oman',
  },
  {
    code: 970,
    region: 'PS',
    country: 'Palestine',
  },
  {
    code: 971,
    region: 'AE',
    country: 'United Arab Emirates',
  },
  {
    code: 972,
    region: 'IL',
    country: 'Israel',
  },
  {
    code: 973,
    region: 'BH',
    country: 'Bahrain',
  },
  {
    code: 974,
    region: 'QA',
    country: 'Qatar',
  },
  {
    code: 975,
    region: 'BT',
    country: 'Bhutan',
  },
  {
    code: 976,
    region: 'MN',
    country: 'Mongolia',
  },
  {
    code: 977,
    region: 'NP',
    country: 'Nepal',
  },
  {
    code: 979,
    region: '001',
    country: '-',
  },
  {
    code: 992,
    region: 'TJ',
    country: 'Tajikistan',
  },
  {
    code: 993,
    region: 'TM',
    country: 'Turkmenistan',
  },
  {
    code: 994,
    region: 'AZ',
    country: 'Azerbaijan',
  },
  {
    code: 995,
    region: 'GE',
    country: 'Georgia',
  },
  {
    code: 996,
    region: 'KG',
    country: 'Kyrgyzstan',
  },
  {
    code: 998,
    region: 'UZ',
    country: 'Uzbekistan',
  },
];
